import * as Yup from "yup";
import ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import { FIELD_REQUIRED } from "../../../../../utils/constants";
import { giftNameValidation } from "./prize_validation_schema";

const loyaltyPrizeValidation = Yup.object({
  name: giftNameValidation,
  image: Yup.string().required(FIELD_REQUIRED),
  conditions: Yup.array().of(Yup.string().optional()),
  iQty: Yup.number().optional(),
  expiry: Yup.object().optional()
});

const tokenRewardValidation = Yup.mixed().test(
  "token_reward_validation",
  FIELD_REQUIRED,
  (reward: unknown) => {
    if (typeof reward === "number") {
      return true;
    }

    if (typeof reward === "string") {
      return reward.trim() !== "" && !isNaN(Number(reward));
    }

    return false;
  }
);

const tierValidation = Yup.object({
  reward: Yup.lazy((reward: unknown) =>
    typeof reward === "number" || typeof reward === "string"
      ? tokenRewardValidation
      : loyaltyPrizeValidation
  )
});

const leaderboardValidation = Yup.lazy((leaderboardSpecialPrize: unknown) => {
  if (leaderboardSpecialPrize == null) {
    return Yup.mixed().optional();
  }

  if (
    typeof leaderboardSpecialPrize !== "object" ||
    Array.isArray(leaderboardSpecialPrize)
  ) {
    return Yup.mixed().required(FIELD_REQUIRED);
  }

  const prizeConfig = leaderboardSpecialPrize as {
    type?: string;
    amount?: number | string;
  };

  // Backward compatibility: old payloads can have amount without type.
  if (
    prizeConfig.type == null &&
    prizeConfig.amount !== undefined &&
    prizeConfig.amount !== null
  ) {
    return Yup.object({
      amount: tokenRewardValidation
    });
  }

  if (prizeConfig.type === "tokens") {
    return Yup.object({
      type: Yup.string().oneOf(["tokens"]).required(FIELD_REQUIRED),
      amount: tokenRewardValidation
    });
  }

  if (prizeConfig.type === "prize") {
    return Yup.object({
      type: Yup.string().oneOf(["prize"]).required(FIELD_REQUIRED),
      prize: loyaltyPrizeValidation.required(FIELD_REQUIRED)
    });
  }

  return Yup.object({
    type: Yup.string().oneOf(["tokens", "prize"]).required(FIELD_REQUIRED)
  });
});

export default function createLoyaltyPrizeValidationSchema(
  accountToDisplay?: ManagedAccount | null
) {
  const shouldValidateLeaderboardPrize =
    accountToDisplay?.saasFeatures?.ltycm === true;

  const schema = Yup.object({
    lty: Yup.object({
      enabled: Yup.boolean().optional(),
      tiers: Yup.array()
        .of(tierValidation)
        .when("enabled", {
          is: true,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.optional()
        }),
      leaderboardEnabled: Yup.boolean().optional(),
      leaderboardSpecialPrize: Yup.mixed()
        .nullable()
        .test(
          "leaderboard-special-prize-validation",
          FIELD_REQUIRED,
          function (value) {
            const { leaderboardEnabled } = this.parent;

            // Only validate if BOTH conditions are met
            if (!leaderboardEnabled || !shouldValidateLeaderboardPrize) {
              return true; // Pass validation - field is optional
            }

            // Both conditions are met, validate the value
            if (value == null || value === undefined) {
              return false; // Fail validation - value is required
            }

            // Value exists, validate its structure
            try {
              leaderboardValidation.validateSync(value, { abortEarly: false });
              return true;
            } catch (error) {
              // Preserve the nested path from the validation error
              if (error instanceof Yup.ValidationError) {
                if (error.inner && error.inner.length > 0) {
                  // If there are multiple errors, create errors for each with proper paths
                  const errors = error.inner.map(err => {
                    return this.createError({
                      path: err.path ? `${this.path}.${err.path}` : this.path,
                      message: err.message
                    });
                  });
                  // Return the first error (Yup will collect all of them)
                  return errors[0];
                } else if (error.path) {
                  // Single error with a path
                  return this.createError({
                    path: `${this.path}.${error.path}`,
                    message: error.message
                  });
                }
              }
              return this.createError({
                message: error instanceof Error ? error.message : FIELD_REQUIRED
              });
            }
          }
        )
    }).optional()
  });

  return schema;
}
